import type { JSONContent } from "@tiptap/core";

export interface AgentEditUndoSnapshot {
  doc: JSONContent;
  nativeUndoDepthAtCapture: number;
  postAgentDoc?: JSONContent;
}

export interface AgentEditRedoSnapshot {
  doc: JSONContent;
  nativeRedoDepthAtRestore: number;
}

/**
 * A committed snapshot represents an earlier agent edit and must not block the next edit's capture.
 * Only an uncommitted pending snapshot means the current edit wave was already captured.
 */
export function shouldCaptureAgentEditUndoSnapshot(params: {
  hasPendingSnapshot: boolean;
  pendingSnapshotCommitted: boolean;
}): boolean {
  return !(params.hasPendingSnapshot && !params.pendingSnapshotCommitted);
}

/**
 * Determines whether native TipTap undo should fire before consuming an agent snapshot.
 *
 * When a Chat/Allow Edits operation captures a pre-agent snapshot, we record the
 * ProseMirror native undoDepth at that moment. If the user then types, cuts, pastes,
 * or deletes, the native undoDepth grows. At undo time, if current depth > captured
 * depth, the native (more recent) edits should be undone first.
 */
export function shouldUseNativeUndo(currentUndoDepth: number, topSnapshot: Pick<AgentEditUndoSnapshot, "nativeUndoDepthAtCapture"> | null | undefined) {
  if (!topSnapshot) return true;
  return currentUndoDepth > topSnapshot.nativeUndoDepthAtCapture;
}

/**
 * Determines whether native TipTap redo should fire before consuming an agent redo snapshot.
 */
export function shouldUseNativeRedo(currentRedoDepth: number, topRedoSnapshot: Pick<AgentEditRedoSnapshot, "nativeRedoDepthAtRestore"> | null | undefined) {
  if (!topRedoSnapshot) return true;
  return currentRedoDepth > topRedoSnapshot.nativeRedoDepthAtRestore;
}

/**
 * Builds the array of redo entries that applyAgentEditUndoSnapshot pushes onto the redo stack.
 *
 * When an agent undo fires after newer native edits have been undone, its full-document replacement
 * can leave those native redo entries mapped to no-ops. Preserve every state captured before those
 * native undos as custom fallback entries so redo still advances one chronological change at a time.
 *
 * Returns entries in push order: [newest manual state ... oldest manual state, current agent state].
 * The stack is LIFO, so the current agent state is restored first, followed by each manual state.
 * Every entry shares the current native redo depth. Native entries above that boundary represent
 * changes made before the agent edit and must still redo first; entries at the boundary are the
 * newer changes represented by these snapshots and must not add invisible native redo clicks.
 */
export function buildRedoEntries(currentDoc: JSONContent, currentRedoDepth: number, nativeUndoDocs: JSONContent[] = []): AgentEditRedoSnapshot[] {
  return [...nativeUndoDocs, currentDoc].map((doc) => ({
    doc,
    nativeRedoDepthAtRestore: currentRedoDepth,
  }));
}

/**
 * Crossing an older agent boundary can add more native redo entries for manual changes that are
 * already represented by custom snapshots. Move every later custom entry to the newest boundary
 * so those mapped native entries cannot add no-op redo clicks between chronological states.
 */
export function rebaseRedoEntries(entries: AgentEditRedoSnapshot[], nativeRedoDepth: number): AgentEditRedoSnapshot[] {
  return entries.map((entry) => ({
    ...entry,
    nativeRedoDepthAtRestore: nativeRedoDepth,
  }));
}

/**
 * Extracts plain text from a TipTap/ProseMirror JSON doc by walking all text nodes.
 * Concatenates without separators, matching ProseMirror's `doc.textContent` behavior.
 * Used for text-only comparison when full JSON comparison fails due to mark stripping
 * during Ydoc round-trips (e.g., "inserted" marks removed by stripUnknownMarks).
 */
export function extractTextFromDocJson(docJson: unknown): string {
  if (!docJson || typeof docJson !== "object") return "";
  const parts: string[] = [];
  const walk = (node: unknown): void => {
    if (!node || typeof node !== "object") return;
    const n = node as { type?: string; text?: string; content?: unknown[] };
    if (n.type === "text" && n.text) {
      parts.push(n.text);
    }
    if (Array.isArray(n.content)) {
      n.content.forEach((child) => walk(child));
    }
  };
  walk(docJson);
  return parts.join("");
}
